import styled from 'styled-components';

import { sweepTableColumnsFor } from 'components/inboundReferrals/constants';
import { usePortalSystems } from 'hooks/usePortalSystems';
import { simpleDateAtTime } from 'lib/date';
import { BodySmall, BodySmallBold, LabelSmallBold } from 'styles/typography';

import DetailSlideout from '../DetailSlideout';

import { ScanCard } from './scanCards';

// What a cell renders when the sweep didn't capture that field. An em dash, never a blank —
// a blank cell reads as a rendering fault rather than as missing data.
const MISSING = '–';

// The sweep stores the portal's string verbatim, age suffix and all ("09/15/1958 (67 yr)");
// the design draws a bare date, so the parenthetical is trimmed at display only.
const bareDob = (value: string) => value.replace(/\s*\([^)]*\)\s*$/, '');

interface Props {
  card: ScanCard | null;
  onClose: () => void;
  // Opens the task the row's referral became. Every row has one: the api writes a workflow id
  // exactly for the `created` disposition, and no other disposition reaches this table.
  onOpenTask: (workflowId: string, siblingWorkflowIds: string[]) => void;
}

// A cell's value for one configured column. `patient_name` prefers the join's own resolution
// (the disposition's name beats the discovered entry's); everything else reads the raw sweep
// entry by the field's camelCased key — configured columns beyond the declared trio arrive
// only that way. Older sweeps that never captured a field keep rendering the em dash.
function cellValue(row: ScanCard['rows'][number], name: string): string {
  if (name === 'patient_name') return row.patientName ?? '';

  const camel = name.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
  const value = row.discovered?.[camel];
  const text = value == null ? '' : String(value);
  return name === 'patient_dob' ? bareDob(text) : text;
}

/**
 * The results of one scan. The columns are the portal's configured sweep-table selection
 * (OLIO-3074) — a field an admin unchecks disappears here, and one they add renders with a
 * sparse em dash wherever a portal didn't supply it.
 */
export default function ScanResultsSlideout({ card, onClose, onOpenTask }: Props) {
  const { systems: portalSystems } = usePortalSystems();
  const rows = card?.rows ?? [];
  const siblings = rows.map((row) => row.workflowId);
  const columns = sweepTableColumnsFor(portalSystems, card?.sourceSystem ?? '');
  // Every row's referral stays reachable whatever the configured columns: the patient-name
  // column is the link when present, else the first column carries it.
  const linkColumn = columns.find((column) => column.name === 'patient_name') ?? columns[0];

  return (
    <DetailSlideout
      open={!!card}
      onClose={onClose}
      // The header repeats the card the person just clicked: the outcome sentence on top, the
      // provenance line under it, and the count sits above the table as its own line.
      title={card ? card.title : 'Scan results'}
      subtitle={card?.completedAt ? `Portal successfully scanned on ${simpleDateAtTime(card.completedAt)}` : undefined}
      hideBackButton>
      <Count data-testid='scan-results-count'>{card?.detail}</Count>
      <Table data-testid='scan-results-table'>
        <thead>
          <tr>
            {columns.map((column) => (
              <Th key={column.name}>{column.label}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-testid='scan-result-row'>
              {columns.map((column) => (
                <Td key={column.name} data-testid={`scan-result-${column.name}`}>
                  {column.name === linkColumn?.name ? (
                    <NameLink
                      type='button'
                      onClick={() => onOpenTask(row.workflowId, siblings)}
                      data-testid='scan-result-name'>
                      {cellValue(row, column.name) || MISSING}
                    </NameLink>
                  ) : (
                    cellValue(row, column.name) || MISSING
                  )}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </DetailSlideout>
  );
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled(LabelSmallBold).attrs({ as: 'th' })`
  text-align: left;
  padding: 8px 12px;
  color: ${({ theme }) => theme.colors.black75};
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
`;

const Td = styled(BodySmall).attrs({ as: 'td' })`
  padding: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  color: ${({ theme }) => theme.colors.black100};
`;

const NameLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
`;

const Count = styled(BodySmallBold)`
  display: block;
  margin-bottom: 12px;
`;
